import { Settings } from '@mui/icons-material';
import { Box, Button, Dialog, DialogTitle, Typography } from '@mui/material';
import { Dictionary } from 'lodash';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { useVisible } from '~/hooks/useVisible';
import { IndicatorByUnitResponse, IndicatorCategory } from '~/services/Http';

import { IndicatorCategoryTab } from '../../../../models';

import { ConfigureIndicatorsDialogContent } from './components/ConfigureIndicatorsDialogContent';
import { IndicatorGroup } from './components/IndicatorGroup';
import { useSxStyles } from './useStyles';

export interface Props {
  indicatorDictionary: Dictionary<IndicatorByUnitResponse[]>;
  selectedCategoryTab: IndicatorCategoryTab;
  unitId: number;
  containerWidth: number;
}

export const IndicatorsContainer: React.FC<Props> = ({
  indicatorDictionary,
  selectedCategoryTab,
  unitId,
  containerWidth,
}) => {
  const sxStyles = useSxStyles();
  const { t } = useTranslation();

  const noOfFittingColumns = Math.floor((containerWidth - 16) / 80);
  const groupButtonWidth = (containerWidth - 16) / noOfFittingColumns;

  const selectedCategory =
    selectedCategoryTab !== IndicatorCategoryTab.ALL &&
    selectedCategoryTab !== IndicatorCategoryTab.CUSTOM
      ? (Object.keys(IndicatorCategory) as IndicatorCategory[]).find(
          (key) => IndicatorCategoryTab[key] === selectedCategoryTab,
        )
      : undefined;

  const {
    isVisible: isConfigureIndicatorsDialogOpen,
    show: openConfigureIndicatorsDialog,
    hide: closeConfigureIndicatorsDialog,
  } = useVisible();

  const filteredAndSortedIndicators = (
    Object.keys(indicatorDictionary) as IndicatorCategory[]
  )
    .filter(
      (k) =>
        selectedCategoryTab === IndicatorCategoryTab.ALL ||
        IndicatorCategoryTab[k] === selectedCategoryTab,
    )
    .sort(
      (k1: IndicatorCategory, k2: IndicatorCategory) =>
        IndicatorCategoryTab[k1] - IndicatorCategoryTab[k2],
    );

  return (
    <Box sx={sxStyles.indicatorsContainer} data-cy="indicators-container">
      {filteredAndSortedIndicators?.length ? (
        filteredAndSortedIndicators.map((key: IndicatorCategory) => (
          <IndicatorGroup
            key={key}
            category={key}
            indicatorGroup={indicatorDictionary[key]}
            groupButtonWidth={groupButtonWidth}
            unitId={unitId}
          />
        ))
      ) : (
        <Typography variant="body2" sx={sxStyles.noActiveIndicatorsMessage}>
          {t('noActiveIndicatorsConfigured')}
        </Typography>
      )}

      {selectedCategory && (
        <Button
          startIcon={<Settings />}
          size="small"
          sx={sxStyles.configureButton}
          onClick={openConfigureIndicatorsDialog}
          data-cy="configure-button"
        >
          {t('configure')}
        </Button>
      )}

      <Dialog
        open={isConfigureIndicatorsDialogOpen}
        onClose={closeConfigureIndicatorsDialog}
        data-cy="configure-dialog"
      >
        <DialogTitle>
          {selectedCategory &&
            `${t(selectedCategory.toLowerCase())} ${t(
              'indicatorsConfiguration',
            )}`}
        </DialogTitle>
        {indicatorDictionary &&
          selectedCategory &&
          isConfigureIndicatorsDialogOpen && (
            <ConfigureIndicatorsDialogContent
              selectedCategory={selectedCategory}
              onSaved={closeConfigureIndicatorsDialog}
              onClose={closeConfigureIndicatorsDialog}
              unitId={unitId}
            />
          )}
      </Dialog>
    </Box>
  );
};
